import { Box, Grid } from "@material-ui/core";
import { Championship, Collections, useCollection } from "api";
import { useFormikContext } from "formik";
import pickBy from "lodash/pickBy";
import MaterialTable from "material-table";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { LoggedUserContext, Message, SelectUsers } from "ui/componentes";
import { AddBox } from "@material-ui/icons";
import { ChampionshipDriver } from "api/model/Championship/ChampionshipDriver";
import { useSnackbar } from "notistack";
import { useFirestore } from "react-redux-firebase";
import { exportCSVChampionshipDrivers } from "ui/views/ChampionshipForm/DriverConfiguration/exportDrivers";
import { LocalizationPTBr } from "util/MaterialTableUtil";

const onSelectUser = (firestore, championship, enqueueSnackbar) => (user) => {
    if (!user?.id) {
        return;
    }
    const { id, firstName, lastName, displayName, country, driverInfo = {} } =
        user || {};
    const { iracingId, preferredNumbers } = driverInfo;
    const driver = {
        userId: id,
        championshipId: championship.id,
        seasonId: championship.seasonId,
        firstName,
        lastName,
        displayName,
        country,
        iracingId,
        preferredNumbers,
        usedNumber: preferredNumbers?.option1
            ? preferredNumbers?.option1 + ""
            : "",
    };
    firestore
        .collection(Collections.CHAMPIONSHIP_DRIVERS)
        .add(pickBy(driver, (value) => value !== undefined))
        .then()
        .catch((e) => {
            console.error(e);
            enqueueSnackbar("Erro ao adicionar piloto!", { variant: "error" });
        });
};

const DriverConfiguration = () => {
    const { isAdmin } = useContext(LoggedUserContext);
    const { values } = useFormikContext<Championship>();
    const firestore = useFirestore();
    const { enqueueSnackbar } = useSnackbar();
    const drivers = useCollection<ChampionshipDriver[]>(
        {
            collection: Collections.CHAMPIONSHIP_DRIVERS,
            where: ["championshipId", "==", values.id || "-1"],
            storeAs: `drivers_${values.id}`,
        },
        true
    );

    const [
        messageCarsNumbersUndefined,
        setMessageCarsNumbersUndefined,
    ] = useState("");
    const [
        messageCarsNumbersDuplicated,
        setMessageCarsNumbersDuplicated,
    ] = useState("");
    const [openSelectUsers, setOpenSelectUsers] = useState(false);

    useEffect(() => {
        if (drivers && drivers.length > 0) {
            const driversWithoutNumber = drivers.filter(
                (driver) => !driver.usedNumber
            );

            let driversWithDuplicatedNumbers = [];

            drivers
                .filter((driver) => driver.usedNumber)
                .forEach((driver) => {
                    const drivers =
                        driversWithDuplicatedNumbers[driver.usedNumber] || [];
                    drivers.push(driver.displayName);
                    driversWithDuplicatedNumbers[driver.usedNumber] = drivers;
                });

            if (
                driversWithDuplicatedNumbers &&
                driversWithDuplicatedNumbers.length > 0
            ) {
                const duplicatedNumbersMessage = driversWithDuplicatedNumbers
                    .map((drivers: ChampionshipDriver[], index) => {
                        if (drivers.length > 1) {
                            return `(Nº ${index} - ${drivers.join(", ")})`;
                        }
                        return "";
                    })
                    .filter((v) => v)
                    .join(" - ");

                if (duplicatedNumbersMessage) {
                    setMessageCarsNumbersDuplicated(
                        `Pilotos com números iguais: ${duplicatedNumbersMessage}`
                    );
                } else {
                    setMessageCarsNumbersDuplicated("");
                }
            }

            if (driversWithoutNumber && driversWithoutNumber.length > 0) {
                const driversMessage = driversWithoutNumber
                    .map((driver) => driver.displayName)
                    .join(", ");

                setMessageCarsNumbersUndefined(
                    `Pilotos sem número definido: [${driversMessage}]`
                );
            } else {
                setMessageCarsNumbersUndefined("");
            }
        }
    }, [drivers]);

    const filterDriversOptions = useMemo(() => {
        return [
            (driver) => !!driver.driverInfo?.iracingId,
            (driver) => {
                if (drivers) {
                    return !drivers
                        .map((d) => d.userId)
                        .includes(driver.id || "");
                }
                return true;
            },
        ];
    }, [drivers]);

    const sortedDrivers = useMemo(
        () =>
            drivers.sort((d1, d2) =>
                d1.displayName < d2.displayName ? -1 : 1
            ),
        [drivers]
    );

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} hidden={!messageCarsNumbersUndefined}>
                <Message
                    variant="error"
                    show={!!messageCarsNumbersUndefined}
                    message={messageCarsNumbersUndefined}
                />
            </Grid>
            <Grid item xs={12} hidden={!messageCarsNumbersDuplicated}>
                <Message
                    variant="error"
                    show={!!messageCarsNumbersDuplicated}
                    message={messageCarsNumbersDuplicated}
                />
            </Grid>
            <Grid item xs={12}>
                <SelectUsers
                    open={openSelectUsers}
                    onSelectUser={onSelectUser(
                        firestore,
                        values,
                        enqueueSnackbar
                    )}
                    onClose={() => setOpenSelectUsers(false)}
                    filterOptions={filterDriversOptions}
                />
            </Grid>
            <Grid item xs={12}>
                <MaterialTable
                    components={{ Container: Box }}
                    data={sortedDrivers}
                    columns={[
                        {
                            field: "iracingId",
                            title: "ID iRacing",
                            align: "right",
                        },
                        {
                            field: "usedNumber",
                            title: "Número",
                            align: "right",
                        },
                        {
                            field: "displayName",
                            title: "Nome",
                        },
                        {
                            field: "teamName",
                            title: "Equipe",
                        },
                        {
                            field: "preferredNumbers",
                            title: "Opções de números",
                            editable: "never",
                            export: false,
                            render: ({ preferredNumbers = {} }) => {
                                const {
                                    option1,
                                    option2,
                                    option3,
                                } = preferredNumbers;
                                const options = [
                                    option1 || "",
                                    option2 || "",
                                    option3 || "",
                                ]
                                    .filter((option) => option)
                                    .join(", ");
                                return options ? `(${options})` : "";
                            },
                        },
                    ]}
                    actions={[
                        {
                            icon: () => <AddBox color="primary" />,
                            onClick: () => setOpenSelectUsers(true),
                            isFreeAction: true,
                            tooltip: "Adicionar piloto",
                            disabled: !values.id,
                        },
                        {
                            icon: "delete",
                            onClick: (event, driver) => {
                                firestore
                                    .collection(
                                        Collections.CHAMPIONSHIP_DRIVERS
                                    )
                                    .doc((driver as ChampionshipDriver).id)
                                    .delete()
                                    .then()
                                    .catch((e) => {
                                        console.error(e);
                                        enqueueSnackbar(
                                            "Error ao remover piloto!",
                                            { variant: "error" }
                                        );
                                    });
                            },
                        },
                    ]}
                    editable={{
                        onRowUpdate: ({ id, ...newDriver }, oldDriver) => {
                            if (oldDriver) {
                                firestore
                                    .collection(
                                        Collections.CHAMPIONSHIP_DRIVERS
                                    )
                                    .doc(id)
                                    .update(newDriver)
                                    .then()
                                    .catch((e) => {
                                        console.error(e);
                                        enqueueSnackbar(
                                            "Error ao atualizar piloto!",
                                            { variant: "error" }
                                        );
                                    });
                            }
                            return Promise.resolve();
                        },
                    }}
                    localization={LocalizationPTBr}
                    options={{
                        showTitle: false,
                        filtering: true,
                        search: false,
                        pageSize: 10,
                        draggable: false,
                        sorting: false,
                        exportCsv: exportCSVChampionshipDrivers(values.name),
                        exportButton: isAdmin,
                        exportDelimiter: ";",
                    }}
                />
            </Grid>
        </Grid>
    );
};

export { DriverConfiguration };
